import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Inject
} from '@angular/core';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { TranslationService } from 'src/app/angular-localization-v7/services/translation.service';
import { LocalizationComponent } from './components/localization/localization.component';
import { LoaderService } from './services/loader.service';
import { SessionService } from 'src/app/angular-services-v8/session.service';
import { FeatureToggleService } from 'src/app/feature-toggle-service/v1/src/feature-toggle.service';
import { CountlyService } from 'src/app/helpers-v1/countly.service';
import { CustomerService } from './services/';
import { combineLatest, Subject } from 'rxjs/';
import {
  distinctUntilChanged,
  filter,
  map,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AppInsightsService } from './services/app-insights.service';
import { environment } from '../environments/environment';
import { AppFeatures } from '../toggleConfig';
import { Broadcaster } from './events-v7/projects/events-v7/src/public_api';
import { IamAuthService } from './msal-services-v1/services/iam-auth.service';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent
  extends LocalizationComponent
  implements OnInit, OnDestroy, AfterContentChecked
{
  private destroyNotifier$: Subject<void> = new Subject();
  public cobrowseKey: string = environment.cobrowseKey;
  currentCustomer: any = 'Maria';
  public showCobrowse = false;
  constructor(
    private router: Router,
    public translationService: TranslationService,
    private featureToggleService: FeatureToggleService,
    public loaderService: LoaderService,
    private activatedRoute: ActivatedRoute,
    private customerService: CustomerService,
    private session: SessionService,
    private countly: CountlyService,
    private appInsights: AppInsightsService,
    private cdref: ChangeDetectorRef,
    public eventDispatcher: Broadcaster,
    private iamService: IamAuthService,
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
    this.cmxTranslation = this.translationService;
    try {
      const $htmlElement = document.getElementsByTagName('html');
      if (this.cmxTranslation.selectedLanguage.rtl) {
        $htmlElement[0].dir = 'rtl';
      } else {
        $htmlElement[0].dir = 'ltr';
      }
    } catch ($exception) {
      console.error($exception);
    }
  }

  ngOnInit() {
    let url = window.location.hostname;
    const TRANSPARENT_ICON = 'data:image/x-icon;base64,Qk0yAAAAAAAAADYAAAAoAAAAAgAAAAIAAAABABgAAAAAAAAAAADEDgAAxA4AAAAAAAAAAAAA////AP///wD///8A////AA==';
    const linkElement = this.document.querySelector("link[rel='icon']") as HTMLLinkElement;

    if (url.includes('localhost')) {
      this.titleService.setTitle('CMXGO - Localhost');
      if (linkElement) {
        linkElement.href = TRANSPARENT_ICON;
        setTimeout(() => linkElement.remove(), 0); 
      }
    }
    else 
    if (url.includes('pagosenlineaco')) {
      this.titleService.setTitle('Pagos en Linea');
      if (linkElement) {
        linkElement.href = TRANSPARENT_ICON;
        setTimeout(() => linkElement.remove(), 0); 
      }
    }

    this.iamService.handleMsalEvents();
    this.appInsights.loadAppInsights();
    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    }); // window['dataLayer']
    if (window as { [key: string]: any }['dataLayer']) {
      this.sendDataToGoogleTagManager();
    }
    this.setCobrowseFeature();
  }

  ngOnDestroy(): void {
    this.destroyNotifier$.next();
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  private sendDataToGoogleTagManager(): void {
    const segmentationReady$ = this.customerService.segmentationsStatus$.pipe(
      filter((status) => status === 'loaded' || status === 'error'),
      take(1),
    );

    const routerUrl$ = this.router.events.pipe(
      filter(
        (routerEvent): routerEvent is NavigationEnd =>
          routerEvent instanceof NavigationEnd,
      ),
      map((routerEvent) => routerEvent.urlAfterRedirects || routerEvent.url),
      distinctUntilChanged(),
    );

    const gtm$ = combineLatest([routerUrl$, segmentationReady$]).pipe(
      map(([routerUrl]) => {
        const routerData = this.getDeepRouterData();
        if (!this.session.isLoggedIn || !routerData?.['track']) {
          return undefined;
        }
        return { routerData, routerUrl };
      }),
      filter((event) => event !== undefined),
      tap(({ routerData, routerUrl }) => {
        const isUserManagement = routerUrl.includes('user-management');
        const application = isUserManagement
          ? 'User_Management'
          : 'Cross_Services';
        const pageTitle = routerData['routeName'] ?? '';
        this.countly.addGTM(application, pageTitle);
      }),
      takeUntil(this.destroyNotifier$),
    );

    gtm$.subscribe();
  }

  private getDeepRouterData(): Data | undefined {
    let childRoute = this.activatedRoute.firstChild;
    while (childRoute) {
      if (childRoute.firstChild) {
        childRoute = childRoute.firstChild;
      } else if (childRoute.snapshot) {
        return childRoute.snapshot.data;
      }
    }
    return undefined;
  }

  private setCobrowseFeature() {
    const country = this.session.userCountry;
    if (country) {
      this.featureToggleService.isLoaded$.subscribe(async () => {
        this.showCobrowse = this.featureToggleService.isEnabledFor(
          AppFeatures.CoBrowse,
          { country },
        );
      });
    }
  }

  private isFeatureToggleLoaded$ = this.featureToggleService.isLoaded$.pipe(
    filter((isLoaded) => isLoaded),
  );

  public isNewDashboard2025Enabled$ = this.isFeatureToggleLoaded$.pipe(
    map(() => {
      if (this.session.userCountry) {
        return this.featureToggleService.isEnabledFor(
          AppFeatures.NewDashboard2025,
          {
            country: this.session.userCountry,
          },
        );
      }

      return false;
    }),
  );
}
